/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 11th 2022, 12:06:39 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { FormOptionsSeparator, Image } from "ui-widgets";
import { OSDropZone, FileDropTarget } from "ui-widgets/drag-and-drop";

interface FilePreviewProps {
  validFileIcon?: React.ReactNode;
  validFileMessage: React.ReactNode;
  invalidFileIcon?: React.ReactNode;
  invalidFileMessage: React.ReactNode;
}

const ValidFilePreview = ({ validFileMessage, validFileIcon }: FilePreviewProps) => (
  <Stack spacing={1}>
    <div>{validFileIcon || <Image src="/img/valid.png" width={80} height={80} />}</div>
    {validFileMessage}
  </Stack>
);

const InvalidFile = ({
  invalidFileIcon,
  invalidFileMessage,
}: Pick<FilePreviewProps, "invalidFileMessage" | "invalidFileIcon">) => (
  <Stack spacing={1}>
    <div>{invalidFileIcon || <Image src="/img/corrupt-file.png" width={80} height={80} />}</div>
    {invalidFileMessage}
  </Stack>
);

interface OSFileInputProps {
  dropTargetText: string;
  isValid: boolean;
  uploadedFiles: File[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  filePreviewProps: FilePreviewProps;
  chooseButtonLabel?: string;
  multiple?: boolean;
}

export const OSFileInput = ({
  dropTargetText,
  filePreviewProps,
  isValid,
  uploadedFiles,
  setUploadedFiles,
  chooseButtonLabel,
  multiple = false,
}: OSFileInputProps) => {
  const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) =>
    setUploadedFiles(event.target.files as unknown as File[]);

  return (
    <>
      <FileDropTarget
        onDrop={({ files }) => {
          setUploadedFiles(files);
        }}
      >
        {({ isActive }) => {
          const dropText = isActive ? "Release to drop" : dropTargetText;

          const showDropMessage = !uploadedFiles || (isActive && !isValid);

          return (
            <OSDropZone
              style={{
                minHeight: 200,
              }}
            >
              {showDropMessage ? (
                <>
                  <p>
                    <Image src={isActive ? "/img/drop-file.png" : "/img/upload-file.png"} width={80} height={80} />
                  </p>
                  <p>{dropText}</p>
                </>
              ) : isValid ? (
                <ValidFilePreview {...filePreviewProps} />
              ) : (
                <InvalidFile
                  invalidFileMessage={filePreviewProps?.invalidFileMessage}
                  invalidFileIcon={filePreviewProps?.invalidFileIcon}
                />
              )}
            </OSDropZone>
          );
        }}
      </FileDropTarget>
      <FormOptionsSeparator seperatorText="OR" />
      <Button fullWidth variant="outlined" component="label">
        {chooseButtonLabel ? chooseButtonLabel : <>Choose From Your Computer</>}
        <input type="file" hidden onChange={onChangeFile} multiple={multiple} />
      </Button>
    </>
  );
};
